import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { useDoctors, useUpdateDoctor } from '@/hooks/useDoctors'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import type { DoctorKind, DoctorRow, DoctorBranchCode } from '@/lib/api-client'

export const Route = createFileRoute('/roster')({
  component: RosterPage,
})

// Branch id → code (the roster API filters by branchCode, not id).
const BRANCH_CODE: Record<BranchId, DoctorBranchCode | undefined> = {
  all: undefined,
  'shah-alam': 'shah-alam',
  putrajaya: 'putrajaya',
  bangi: 'bangi',
  'kuala-lumpur': 'kuala-lumpur',
}

const KIND_STYLE: Record<DoctorKind, { bg: string; label: string }> = {
  doctor: { bg: '#3b82f6', label: 'Doctor' },
  assistant: { bg: '#10b981', label: 'Assistant' },
  virtual: { bg: '#6b7280', label: 'Virtual' },
}

const KIND_FILTERS: Array<{ value: DoctorKind | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'doctor', label: 'Doctors' },
  { value: 'assistant', label: 'Assistants' },
  { value: 'virtual', label: 'Virtual' },
]

function RosterPage() {
  const { branchId } = useBranch()
  const branchCode = BRANCH_CODE[branchId]

  const [kind, setKind] = React.useState<DoctorKind | 'all'>('all')
  const [activeOnly, setActiveOnly] = React.useState(false)

  const { data, isLoading } = useDoctors({
    branchCode,
    kind: kind === 'all' ? undefined : kind,
    activeOnly,
  })

  const rows = data?.data ?? []

  // Group by branch (order preserved by API: branch id, then sort_order).
  const groups = React.useMemo(() => {
    const map = new Map<string, { branchName: string; rows: DoctorRow[] }>()
    for (const r of rows) {
      const key = r.branchCode
      if (!map.has(key)) map.set(key, { branchName: r.branchName ?? r.branchCode, rows: [] })
      map.get(key)!.rows.push(r)
    }
    return [...map.entries()]
  }, [rows])

  const subtitle = isLoading
    ? 'Loading...'
    : `${rows.length} lanes${branchCode ? '' : ' across all branches'}`

  return (
    <div className="flex flex-col h-full">
      <Header title="Doctor Roster" subtitle={subtitle} />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4 overflow-y-auto">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {KIND_FILTERS.map((k) => (
              <button
                key={k.value}
                onClick={() => setKind(k.value)}
                className={
                  'rounded-md px-3 py-1 text-[13px] transition-colors ' +
                  (kind === k.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-muted hover:bg-hover')
                }
              >
                {k.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-[13px] text-text-dark">
            <Switch checked={activeOnly} onCheckedChange={setActiveOnly} />
            Active only
          </label>
        </div>

        {isLoading && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
            Loading roster...
          </div>
        )}

        {!isLoading && rows.length === 0 && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
            No lanes. Run the Avixo doctor sync or the seed script.
          </div>
        )}

        {groups.map(([code, group]) => (
          <BranchGroup key={code} branchName={group.branchName} rows={group.rows} />
        ))}
      </div>
    </div>
  )
}

function BranchGroup({ branchName, rows }: { branchName: string; rows: DoctorRow[] }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between border-b border-border bg-background/50 px-4 py-2.5">
        <h3 className="text-[13px] font-semibold text-text-dark">{branchName}</h3>
        <span className="text-xs text-text-muted">{rows.length} lanes</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left text-[11px] uppercase tracking-wide text-text-muted">
              <th className="px-4 py-2 w-10">#</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Kind</th>
              <th className="px-4 py-2">Flags</th>
              <th className="px-4 py-2">Notes</th>
              <th className="px-4 py-2 text-right">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <DoctorRowItem key={r.id} row={r} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DoctorRowItem({ row }: { row: DoctorRow }) {
  const update = useUpdateDoctor()
  const [notes, setNotes] = React.useState(row.notes ?? '')
  const kindStyle = KIND_STYLE[row.kind]

  // Keep local notes in sync if the row refetches with a new value.
  React.useEffect(() => {
    setNotes(row.notes ?? '')
  }, [row.notes])

  const commitNotes = () => {
    const next = notes.trim() === '' ? null : notes.trim()
    if (next !== (row.notes ?? null)) {
      update.mutate({ id: row.id, notes: next })
    }
  }

  return (
    <tr className="border-b border-border last:border-0 hover:bg-hover/30">
      <td className="px-4 py-2.5 text-text-muted text-xs">{row.sortOrder ?? '-'}</td>
      <td className="px-4 py-2.5">
        <div className={'font-medium text-[13px] ' + (row.active ? '' : 'text-text-muted line-through')}>
          {row.name}
        </div>
        {row.avixoLaneId != null && (
          <div className="text-[11px] text-text-muted">lane #{row.avixoLaneId}</div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <Badge style={{ background: kindStyle.bg, color: 'white' }}>{kindStyle.label}</Badge>
      </td>
      <td className="px-4 py-2.5">
        {row.isFloating && (
          <Badge variant="outline" title="Appears in 2+ branches">
            ✈ Floating
          </Badge>
        )}
      </td>
      <td className="px-4 py-2.5">
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={commitNotes}
          placeholder="Add note…"
          className="h-8 text-[13px]"
        />
      </td>
      <td className="px-4 py-2.5 text-right">
        <Switch
          checked={row.active}
          disabled={update.isPending}
          onCheckedChange={(v) => update.mutate({ id: row.id, active: v })}
        />
      </td>
    </tr>
  )
}
